import type { HourEntry, PayoutStatus, PayRateType } from '@workflow/contracts'
import type { StatusHue } from '@workflow/ui'

/** Money as the studio's sheets wrote it: the number alone, no currency mark. */
export function money(minor: number | null): string {
  if (minor === null) return ''
  return new Intl.NumberFormat('en', { maximumFractionDigits: 2 }).format(minor / 100)
}

/** An amount as people read it on a page, closed the Bangladeshi way: "160/=". Grids and files keep the bare number. */
export function amountText(minor: number | null): string {
  return minor === null ? '' : `${money(minor)}/=`
}

/** "80/h", "500 once", or nothing while the job has no rate. */
export function rateWords(type: PayRateType | null, amount: number | null): string {
  if (type === null || amount === null) return ''
  return type === 'hourly' ? `${money(amount)}/h` : `${money(amount)} once`
}

export const hoursText = (value: number) => String(Math.round(value * 100) / 100)

/** The colour each pay state wears; unpaid wears none. */
export const PAYOUT_HUE: Record<PayoutStatus, StatusHue | null> = {
  unpaid: null,
  on_hold: 'amber',
  paid: 'green',
  cancelled: 'red',
}

export interface DayLine {
  /** The day in the reader's own calendar, yyyy-mm-dd; null for hours the log cannot date. */
  day: string | null
  hours: number
}

const localDay = (iso: string): string => {
  const date = new Date(iso)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

/**
 * Which days the hours a line pays for were logged on. The log holds every
 * change ever made, and the line pays only the latest unpaid ones up to the
 * period's end — so the walk goes back from the end, newest first, until
 * the line's hours are found. Hours the log cannot date (counted by the
 * studio on completion, or set before the log kept numbers) come last.
 */
export function daysOf(entries: readonly HourEntry[], hours: number, periodEnd: string): DayLine[] {
  const byDay = new Map<string, number>()
  let left = hours
  for (const entry of [...entries].reverse()) {
    if (left <= 0) break
    const day = localDay(entry.at)
    if (day > periodEnd) continue
    const taken = entry.hours > 0 ? Math.min(entry.hours, left) : entry.hours
    byDay.set(day, (byDay.get(day) ?? 0) + taken)
    left -= taken
  }
  const out: DayLine[] = [...byDay.entries()]
    .filter(([, value]) => Math.abs(value) > 1e-9)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, value]) => ({ day, hours: Math.round(value * 100) / 100 }))
  if (left > 1e-9) out.push({ day: null, hours: Math.round(left * 100) / 100 })
  return out
}

const dayFormat = new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short' })
export const dayLabel = (day: string): string => dayFormat.format(new Date(`${day}T12:00:00`))

/** "1 Oct – 31 Oct 2026" for a period. */
export function periodLabel(start: string, end: string): string {
  const year = new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short', year: 'numeric' })
  return `${dayLabel(start)} – ${year.format(new Date(`${end}T12:00:00`))}`
}

/** A grid as a CSV file the browser saves — opens in Excel and Google Sheets alike. */
export function downloadCsv(name: string, rows: readonly (readonly (string | number)[])[]): void {
  const cell = (value: string | number) => {
    const text = String(value)
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
  }
  const blob = new Blob([`﻿${rows.map((row) => row.map(cell).join(',')).join('\r\n')}`], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${name.replace(/[^\w\- ]+/g, '').trim() || 'sheet'}.csv`
  link.click()
  URL.revokeObjectURL(url)
}
